import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { useReactToPrint } from "react-to-print";
import styled from "styled-components";
import Button from "../../components/Core/Button/Button";
import Flex from "../../components/Layout/Flex";
import EmptyState from "../../components/Shared/EmptyState/EmptyState";
import InvoiceJournal from "../../components/Shared/InvoiceJournal/InvoiceJournal";
import LoadingIndicator from "../../components/Shared/LoadingIndicator/LoadingIndicator";
import InvoiceBatch from "../../models/InvoiceBatch";
import { useFetchInvoiceBatch } from "../../networking/invoice.hooks";

interface InvoiceJournalParamProps {
	id?: string;
}

const InvoiceJournalPage: React.FC = () => {
	const { id } = useParams<keyof InvoiceJournalParamProps>();
	const { mutateAsync: fetchInvoiceBatch } = useFetchInvoiceBatch();
	const { t } = useTranslation();

	const [loading, setLoading] = useState<boolean>(false);
	const [noData, setNoData] = useState<boolean>(false);
	const [journalData, setJournalData] = useState<InvoiceBatch>();

	const wrapperRef = useRef(null);

	const handlePrint = useReactToPrint({
		content: () => wrapperRef.current,
	});

	useEffect(() => {
		const initInvoiceBatch = async () => {
			if (id) {
				setLoading(true);
				const batch = await fetchInvoiceBatch({ invoiceBatchId: id }).catch(() => undefined);
				if (batch) {
					setJournalData(batch);
				} else {
					setNoData(true);
				}
				setLoading(false);
			}
		};

		initInvoiceBatch();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [id]);

	return (
		<SalaryJournalPageWrapper className="p-4 px-6">
			{loading && <LoadingIndicator />}
			{journalData && (
				<Flex margin="0 0 7px 0" direction="row" justifyContent="center">
					<JournalWrapper>
						<InvoiceJournal invoiceBatch={journalData} inputRef={wrapperRef} />
						<ButtonWrapper>
							<Button
								height="35px"
								margin="0 0 0 20px"
								icon="print-white"
								iconSize="12px"
								onClick={handlePrint}
							>
								{t("salary.journal.print")}
							</Button>
						</ButtonWrapper>
					</JournalWrapper>
				</Flex>
			)}
			{noData && <EmptyState margin="2rem 0" label="salary.journal.noData" />}
		</SalaryJournalPageWrapper>
	);
};

export default InvoiceJournalPage;

const SalaryJournalPageWrapper = styled.div`
	@page {
		margin: 30px 0;
	}

	@media all {
		.pagebreak {
			display: none;
		}
	}

	@media print {
		.pagebreak {
			page-break-before: always;
		}
	}
`;

const JournalWrapper = styled.div`
	position: relative;
`;

const ButtonWrapper = styled.div`
	position: absolute;
	z-index: 2;
	right: 10px;
	top: 10px;
`;
